import { CONSENT_PROVIDER } from '~/constants';
import { analyticsQueueStore } from '~/modules/analytics/analyticsQueueStore';
import { handleException } from '~/modules/utilities/error_utilities';
import { logger } from '~/modules/utilities/logger';
import { CookieConsentCategory, isConsentCategoryGranted } from '~/modules/utilities/cookies';

export const DATA_CLOUD_SDK_SCRIPT_ID = 'data-cloud-sdk';

/** Max time to wait for OneTrust to publish `OnetrustActiveGroups` after C360 init. */
const ONETRUST_READY_MAX_WAIT_MS = 5000;
const ONETRUST_READY_POLL_INTERVAL_MS = 250;

interface TrackingConsent {
    purpose: string;
    provider: string;
    status: string;
}

/**
 * The consent slice of the SalesforceInteractions SDK, which loads from a CDN and ships no types.
 * This shape is asserted, not verified — vendor drift surfaces at runtime, not at `tsc`, which is
 * why both call sites below are wrapped in try/catch.
 *
 * Vendor docs type `updateConsents` as `Consent | Consent[]`
 * (https://developer.salesforce.com/docs/data/salesforce-interactions-sdk/guide/c360a-api-consent.html).
 * We always pass a single-element array so mid-session updates match the `init({ consents: [...] })`
 * array contract used elsewhere in this module.
 */
export interface SalesforceInteractionsSdk {
    ConsentPurpose: { Tracking: string };
    ConsentStatus: { OptIn: string; OptOut: string };
    init: (config: { consents: TrackingConsent[] }) => void;
    updateConsents: (consents: TrackingConsent[]) => void;
}

/**
 * Fail closed for C360 only: require OneTrust to have published `OnetrustActiveGroups` before
 * reporting a grant. The shared `isConsentCategoryGranted` helper fails open when the global is
 * undefined (`undefined !== -1`), which is intentional for Functional cookies (user token, org
 * context, etc.) but unsafe for Advertising-gated Data Cloud tracking.
 *
 * Mirrors the fail-closed *intent* of `optimizely_loader_script.ts`, but deliberately omits that
 * loader's non-`.salesforce.com` host bypass — C360 stays OptOut on QA/staging until OneTrust
 * publishes Advertising, which is the safer default for this privacy boundary.
 */
function hasAdvertisingConsent(): boolean {
    return (
        typeof window.OnetrustActiveGroups === 'string' && isConsentCategoryGranted(CookieConsentCategory.Advertising)
    );
}

function getTrackingConsent(sdk: SalesforceInteractionsSdk): TrackingConsent {
    return {
        purpose: sdk.ConsentPurpose.Tracking,
        provider: CONSENT_PROVIDER,
        status: hasAdvertisingConsent() ? sdk.ConsentStatus.OptIn : sdk.ConsentStatus.OptOut,
    };
}

function updateSalesforceInteractionsConsent(sdk: SalesforceInteractionsSdk): void {
    try {
        sdk.updateConsents([getTrackingConsent(sdk)]);
    } catch (error) {
        handleException(error, 'datacloudSdk.updateSalesforceInteractionsConsent', { level: 'warn' });
    }
}

/**
 * OneTrust change listeners cover mid-session preference updates, but a returning visitor who
 * already granted Advertising gets `OnetrustActiveGroups` published without a change event. If
 * C360 inits before that publish (Data Cloud script is earlier in the head than OneTrust), we
 * would otherwise sit at OptOut for the whole page view. Poll briefly for the global, then sync
 * once; cancel if a consent listener already synced.
 */
function scheduleOneTrustReadinessSync(onReady: () => void, isCancelled: () => boolean): void {
    if (typeof window.OnetrustActiveGroups === 'string') {
        return;
    }

    const startTime = Date.now();
    const poll = (): void => {
        if (isCancelled()) {
            return;
        }
        if (typeof window.OnetrustActiveGroups === 'string') {
            onReady();
            return;
        }
        if (Date.now() - startTime >= ONETRUST_READY_MAX_WAIT_MS) {
            return;
        }
        setTimeout(poll, ONETRUST_READY_POLL_INTERVAL_MS);
    };
    setTimeout(poll, ONETRUST_READY_POLL_INTERVAL_MS);
}

function initSalesforceInteractions(): void {
    const sdk = window.SalesforceInteractions;
    if (!sdk) {
        return;
    }

    // Keep init failure separate from listener registration so a thrown init cannot leave the
    // session looking "initialized" without a mid-session consent update path — and so we only
    // attach listeners after a confirmed successful init.
    let initSucceeded = false;
    try {
        sdk.init({
            consents: [getTrackingConsent(sdk)],
        });
        initSucceeded = true;
    } catch (error) {
        // warn: SDK init is anticipated degradation — analytics queue gates itself off, page is unaffected.
        handleException(error, 'datacloudSdk.initSalesforceInteractions', { level: 'warn' });
    }
    if (!initSucceeded) {
        return;
    }

    // Shared cancel flag so the readiness poll and consent listeners cannot double-apply the
    // first sync; mid-session listener fires still call update after the flag is set.
    let readinessSynced = false;
    const onConsentChanged = (): void => {
        readinessSynced = true;
        updateSalesforceInteractionsConsent(sdk);
    };
    const onOneTrustReady = (): void => {
        if (readinessSynced) {
            return;
        }
        readinessSynced = true;
        updateSalesforceInteractionsConsent(sdk);
    };
    // Both OneTrust channels are intentional: `consent.onetrust` covers our window-level bridge,
    // `OnConsentChanged` covers preference-center updates that may not re-dispatch the DOM event.
    // A single user action can fire both; updateConsents is idempotent for the same status.
    window.addEventListener('consent.onetrust', onConsentChanged);
    window.OneTrust?.OnConsentChanged?.(onConsentChanged);
    scheduleOneTrustReadinessSync(onOneTrustReady, () => readinessSynced);
}

/**
 * Initializes the Data Cloud (SalesforceInteractions) SDK once its async script has loaded,
 * and gates the analytics queue on SDK readiness so events triggered before the script
 * finishes are queued and flushed rather than dropped.
 *
 * The script is `async` in `<head>` while this code is reached via a deferred dynamic
 * `import('./boot_client')`, so the script can finish (or fail) before we attach listeners.
 * To avoid a permanently stuck gate we cover every ordering: SDK already present, a script
 * that will never produce an SDK (missing tag in tests, or the empty `data:` placeholder the
 * server substitutes for an empty/blocked URL), the normal load/error events, and a load that
 * already fired before listener attachment (re-checked once below).
 */
export function initDataCloudSdk(): void {
    analyticsQueueStore.setIsDataCloudSdkLoading(true);

    // Settle the gate exactly once, regardless of which path resolves it first. The idempotency
    // guard lets the `{ once: true }` listeners and the post-attach re-check coexist safely.
    let settled = false;
    const settle = (shouldInit: boolean): void => {
        if (settled) {
            return;
        }
        settled = true;
        if (shouldInit) {
            initSalesforceInteractions();
        }
        analyticsQueueStore.setIsDataCloudSdkLoading(false);
    };

    // SDK already present (synchronous/cached execution before boot ran): init now.
    if (window.SalesforceInteractions) {
        settle(true);
        return;
    }

    // eslint-disable-next-line @lwc/lwc/no-document-query
    const script = document.getElementById(DATA_CLOUD_SDK_SCRIPT_ID) as HTMLScriptElement | null;

    // No real SDK will ever arrive: the tag is absent (unit tests only), or its src is the empty
    // `data:` placeholder the server substitutes when SALESFORCE_INTERACTIONS_SCRIPT_URL is
    // empty/blocked/misconfigured (serve_index.ts -> sanitizeScriptUrl). Unblock the queue now.
    if (!script || !script.src || script.src.startsWith('data:')) {
        settle(false);
        return;
    }

    script.addEventListener('load', () => settle(true), { once: true });
    script.addEventListener(
        'error',
        () => {
            // The browser script 'error' event does not provide a JavaScript Error object — the
            // handler receives a generic Event with no name/message/stack. Per the PR scope gate,
            // plain-message logs stay as logger.warn rather than wrapping a string in new Error().
            // logger.warn still routes to Splunk with standard log fields; the structured
            // ExceptionPayload is reserved for call sites that catch a real thrown Error.
            logger.warn('Failed to load SalesforceInteractions SDK script');
            settle(false);
        },
        { once: true },
    );

    // The async script may have fired 'load' before the listeners above were attached — in which
    // case it never fires again. Re-check the global once so a load-already-fired SDK still inits
    // (and clears the gate) instead of leaving events queued forever.
    if (window.SalesforceInteractions) {
        settle(true);
    }
}
